import React, {ChangeEvent} from 'react';
import {curry} from 'ramda';

import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {ToggleSelectedMarker} from 'VideoAudioQC/Timeline/Redux/TimelineActions';
import {TimelineRowType} from 'VideoAudioQC/Timeline/TimelineTypes';
import wrap from 'wraptile';
import {batch} from 'react-redux';
import {OnEditMarkerProps} from '../VideoAudioQCContainer';
import QCTabs from './QCTabs';
import {SetActiveTab} from './Redux/QCTabsActions';
import {TabIndex} from './QCTabsTypes';

interface QCTabsContainerProps {
    item: Item;
    activeTab: TabIndex;
    selectedRowId: string;
    selectedMarkerId: string;
    onCreateQCNote: (rowId: string) => void;
    onCreateAdBreak: (rowId: string) => void;
    onCreateMarkup: (rowId: string) => void;
    onEditMarkerIn: (props: OnEditMarkerProps) => void;
    onEditMarkerOut: (props: OnEditMarkerProps) => void;
    onGoToMarker: (frame: number) => Promise<void>;
    setActiveQCTab: SetActiveTab;
    toggleSelectedMarker: ToggleSelectedMarker;
    jobDeliveryDate: string;
}

export interface OnSetActiveTabProps {
    setActiveQCTab: SetActiveTab;
    toggleSelectedMarker: ToggleSelectedMarker;
    activeTab: TabIndex;
    selectedRowId: string;
    selectedMarkerId: string;
}

export const dispatchSetActiveTabActions = wrap(
    (props: OnSetActiveTabProps, nextActiveTab: number) => {
        const {
            setActiveQCTab,
            toggleSelectedMarker,
            activeTab,
            selectedRowId,
            selectedMarkerId,
        } = props;

        setActiveQCTab(nextActiveTab);

        if (!selectedMarkerId) return;

        switch (activeTab) {
            case TabIndex.QCReport:
                toggleSelectedMarker(
                    selectedMarkerId,
                    selectedRowId,
                    TimelineRowType.QCReport
                );
                break;
            case TabIndex.AdBreaks:
                toggleSelectedMarker(
                    selectedMarkerId,
                    selectedRowId,
                    TimelineRowType.AdBreak
                );
                break;
            case TabIndex.Markup:
                toggleSelectedMarker(
                    selectedMarkerId,
                    selectedRowId,
                    TimelineRowType.Markup
                );
                break;
        }
    }
);

type TabChangeEvent = ChangeEvent<{}>;

export const onSetActiveTab = curry(
    (props: OnSetActiveTabProps, _event: TabChangeEvent, nextActiveTab: number) => {
        batch(dispatchSetActiveTabActions(props, nextActiveTab));
    }
);

function QCTabsContainer(props: QCTabsContainerProps) {
    const {
        item,
        activeTab,
        onCreateQCNote,
        onCreateAdBreak,
        onCreateMarkup,
        onEditMarkerIn,
        onEditMarkerOut,
        onGoToMarker,
        setActiveQCTab,
        toggleSelectedMarker,
        selectedRowId,
        selectedMarkerId,
        jobDeliveryDate,
    } = props;
    const handleSetActiveTab = onSetActiveTab({
        setActiveQCTab,
        toggleSelectedMarker,
        activeTab,
        selectedRowId,
        selectedMarkerId,
    });

    return (
        <QCTabs
            item={item}
            jobDeliveryDate={jobDeliveryDate}
            activeTab={activeTab}
            onEditMarkerIn={onEditMarkerIn}
            onEditMarkerOut={onEditMarkerOut}
            activeTabChange={handleSetActiveTab}
            onCreateQCNote={onCreateQCNote}
            onCreateAdBreak={onCreateAdBreak}
            onCreateMarkup={onCreateMarkup}
            onGoToMarker={onGoToMarker}
        />
    );
}

export default QCTabsContainer;
